import {
  Card,
  Dialog,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  withTheme,
} from '@material-ui/core';
import { CloudDownload, Edit } from '@material-ui/icons';
import { endOfMonth, format, parse, startOfMonth } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import config from '../../config';

import { AscendState } from '../../ducks';
import { FETCH_TRANSACTIONS } from '../../ducks/actions';
import { getSortedMxTransactions } from '../../ducks/mx';
import { dateFormats, getMonths } from '../../ducks/util';
import { MxTransaction } from '../../models/mx';
import { formatFieldValue } from '../shared/format';
import EditTransactionDialog from './EditTransactionDialog';
import { dummyTransactions } from '../dummyData/dummyTransactions';

type StateProps = {
  loading: boolean;
  transactions: MxTransaction[];
  userId: number;
  isMobile: boolean | undefined;
};
type DispatchProps = {
  getTransactions: (userId: number, fromDate: string, toDate: string) => void;
};

type TransactionsTableProps = StateProps & DispatchProps;

const downloadTransactions = (userId: number, fromDate: string, toDate: string) => {
  const url = `${config.apiUrl}/mxTransactions/download`;
  const urlParams = `userId=${userId}&fromDate=${fromDate}&toDate=${toDate}`;
  let xhr = new XMLHttpRequest();
  xhr.open('GET', `${url}?${urlParams}`, true);
  xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
  xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
  xhr.responseType = 'arraybuffer';
  xhr.send();

  xhr.onload = function (e) {
    if (this.status === 200) {
      let blob = new Blob([this.response], { type: 'application/octet-stream' });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'transactions.csv';
      link.click();
    }
  };
};

const getColor = (value: number) => {
  if (value < 0) return 'red';
  if (value > 0) return 'green';
  return 'black';
};

const TransactionsTable = ({
  loading,
  getTransactions,
  transactions,
  userId,
  isMobile,
}: TransactionsTableProps) => {
  const [modalTransaction, setModalTransaction] = useState<MxTransaction | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const [month, setMonth] = useState(() => format(new Date(), dateFormats.yearMonth));
  const monthOptions = getMonths();

  useEffect(() => {
    if (!userId) return;
    const parsedDate = parse(month, dateFormats.yearMonth, new Date());
    const startDate = format(startOfMonth(parsedDate), dateFormats.yearMonthDay);
    const endDate = format(endOfMonth(parsedDate), dateFormats.yearMonthDay);
    getTransactions(userId, startDate, endDate);
  }, [userId, month]);

  useEffect(() => {
    if (!loading) handleClose();
  }, [loading]);

  // todo I'd like the table to be written in a way where the mobile vs desktop differences are handled in a more elegant way
  //  e.g. using a single table with a different number of columns or something like that

  return (
    <Card elevation={2} style={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <EditTransactionDialog onClose={handleClose} transaction={modalTransaction} />
      </Dialog>

      <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'row', gap: '0.25rem' }}>
        <TextField
          margin="dense"
          onChange={(e) => setMonth(e.target.value)}
          select
          value={month}
          variant="outlined">
          {monthOptions.map((monthOption) => (
            <MenuItem key={monthOption.value} value={monthOption.value}>
              {monthOption.label}
            </MenuItem>
          ))}
        </TextField>
        <IconButton
          style={{ height: 'fit-content' }}
          onClick={() => {
            const parsedDate = parse(month, dateFormats.yearMonth, new Date());
            const startDate = format(startOfMonth(parsedDate), dateFormats.yearMonthDay);
            const endDate = format(endOfMonth(parsedDate), dateFormats.yearMonthDay);
            downloadTransactions(userId, startDate, endDate);
          }}>
          <CloudDownload />
        </IconButton>
      </div>

      {!isMobile ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Transaction</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => {
              const amount =
                transaction.type === 'DEBIT' ? -transaction.amount : transaction.amount;
              const rowColor = transaction.status === 'IGNORED' ? 'lightgrey' : 'inherit';
              const description =
                transaction.status === 'IGNORED'
                  ? `${transaction.description} (ignored)`
                  : transaction.description;
              return (
                <TableRow key={transaction.guid} style={{ backgroundColor: rowColor }}>
                  <TableCell>{transaction.date}</TableCell>
                  <TableCell>{description}</TableCell>
                  <TableCell>{transaction.top_level_category}</TableCell>
                  <TableCell style={{ color: getColor(amount), textAlign: 'right' }}>
                    {formatFieldValue(amount, 'number', 'dollar')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="Edit"
                      onClick={() => {
                        setModalTransaction(transaction);
                        setOpen(true);
                      }}>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ paddingLeft: '1rem', paddingRight: '0.25rem' }}>Date</TableCell>
              <TableCell style={{ paddingLeft: '0.25rem', paddingRight: '0.25rem' }}>
                Name
              </TableCell>
              <TableCell
                style={{ maxWidth: '2rem', paddingLeft: '0.25rem', paddingRight: '1.5rem' }}>
                Amount
              </TableCell>
              <TableCell style={{ paddingLeft: '0.5rem', paddingRight: '0' }}>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => {
              const amount =
                transaction.type === 'DEBIT' ? -transaction.amount : transaction.amount;
              const rowColor = transaction.status === 'IGNORED' ? 'lightgrey' : 'inherit';
              const description =
                transaction.status === 'IGNORED'
                  ? `${transaction.description} (ignored)`
                  : transaction.description;
              return (
                <TableRow key={transaction.guid} style={{ backgroundColor: rowColor }}>
                  <TableCell style={{ paddingLeft: '0.25rem', paddingRight: '0.25rem' }}>
                    {formatFieldValue(transaction.date, 'date')}
                  </TableCell>
                  <TableCell
                    style={{
                      maxWidth: '5rem',
                      paddingLeft: '0.25rem',
                      paddingRight: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {description}
                  </TableCell>
                  <TableCell
                    style={{
                      maxWidth: '2rem',
                      paddingLeft: '0.25rem',
                      color: getColor(amount),
                      textAlign: 'right',
                    }}>
                    {formatFieldValue(amount, 'number', 'dollar')}
                  </TableCell>
                  <TableCell
                    style={{ paddingLeft: '0.25rem', paddingRight: '0', textAlign: 'left' }}>
                    <IconButton
                      aria-label="Edit"
                      onClick={() => {
                        setModalTransaction(transaction);
                        setOpen(true);
                      }}>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};

const mapStateToProps = (state: AscendState) => ({
  loading: state.mx.transactions.loading,
  transactions: state.user.data.showDummyData ? dummyTransactions : getSortedMxTransactions(state),
  userId: state.user.data.id,
  isMobile: state.layout.isMobile,
});

// noinspection JSUnusedGlobalSymbols
const mapDispatchToProps = (dispatch: Dispatch) => ({
  getTransactions: (userId: number, fromDate: string, toDate: string) =>
    dispatch({ type: FETCH_TRANSACTIONS, userId, fromDate, toDate }),
});

export default withTheme()(
  connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(TransactionsTable)
);
